import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import req from '@/utils/req';
import { showPop, showPopConfirm } from '@/utils/Pops';
import { Mensagem, Conversa, AgenteSilvaConfig, AgenteSilvaContextValue, AgenteSilvaWidgetProps, CreditosIaSilva, AccaoSilva, VideoSilva, ArtigoSilva } from './types';

const AgenteSilvaContext = createContext<AgenteSilvaContextValue | null>(null);

/**
 * As mensagens vindas do histórico, com a acção de cada resposta do Silva (guardada na
 * `metadata`). Só a da última resposta, sem nada escrito depois dela e ainda dentro da
 * validade, continua à espera: o servidor deita fora o pedido assim que o utilizador escreve
 * outra coisa, e as outras já foram decididas (a decisão está na conversa).
 */
function comAccoesDoHistorico(mensagens: Mensagem[]): Mensagem[] {
    const ultima = mensagens.length - 1;

    return mensagens.map((original, i) => {
        // Os vídeos de ajuda da resposta ficam gravados com ela.
        const videos = Array.isArray(original.metadata?.videos) ? (original.metadata!.videos as VideoSilva[]) : undefined;
        const artigos = Array.isArray(original.metadata?.artigos) ? (original.metadata!.artigos as ArtigoSilva[]) : undefined;
        const m = original.tipo === 'agente' && (videos?.length || artigos?.length) ? { ...original, videos, artigos } : original;
        const accao = m.metadata?.accao_pendente;

        if (m.tipo !== 'agente' || !accao) {
            return m;
        }

        const expirou = accao.expira_em ? new Date(accao.expira_em).getTime() <= Date.now() : false;
        const aEspera = i === ultima && !expirou;

        return { ...m, accao: { ...accao, estado: aEspera ? 'pendente' : expirou && i === ultima ? 'expirada' : 'descartada' } };
    });
}

interface AgenteSilvaProviderProps extends AgenteSilvaWidgetProps {
    children: ReactNode;
}

export function AgenteSilvaProvider({
    children,
    modulo,
    basePath,
    entidadeId,
}: AgenteSilvaProviderProps) {
    const [mensagens, setMensagens] = useState<Mensagem[]>([]);
    const [conversaId, setConversaId] = useState<number | null>(null);
    const [conversas, setConversas] = useState<Conversa[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    // O trinco do envio: o `isLoading` do estado chega tarde a um segundo Enter.
    const aEnviar = useRef(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isHistoryView, setIsHistoryView] = useState(false);
    const [config, setConfig] = useState<AgenteSilvaConfig | null>(null);
    const [creditos, setCreditos] = useState<CreditosIaSilva | null>(null);
    const [aDecidir, setADecidir] = useState<{ accaoId: string; confirmar: boolean } | null>(null);

    const tenantType = modulo === 'logigate' ? 'empresa' : 'escola';

    // Build API base path using basePath prop (context-aware)
    // basePath comes from the layout (e.g., "/empresa-slug" for Logigate, "" for Escola)
    const apiBase = `${basePath}/agente-silva`;

    // Track previous entidade to detect context changes
    const prevEntidadeRef = useRef<{ id: number; modulo: string } | null>(null);

    // Detect context change (user switched empresa/escola)
    useEffect(() => {
        const prevEntidade = prevEntidadeRef.current;

        // If we have a previous context and it changed, reset the chat
        if (prevEntidade && (prevEntidade.id !== entidadeId || prevEntidade.modulo !== modulo)) {
            console.log('Agente Silva: Contexto alterado, reiniciando conversa');
            setMensagens([]);
            setConversaId(null);
            setIsOpen(false);
        }

        // Update ref with current values
        prevEntidadeRef.current = { id: entidadeId, modulo };
    }, [entidadeId, modulo]);

    // Load configuration on mount
    useEffect(() => {
        const carregarConfig = async () => {
            try {
                const response = await req().get(`${apiBase}/config`, {
                    params: { modulo, tenant_type: tenantType }
                });

                if (response.data.estado === 'ok') {
                    setConfig({
                        enabled: response.data.enabled,
                        welcome_message: response.data.welcome_message,
                    });
                    setCreditos(response.data.creditos_ia ?? null);
                }
            } catch (error) {
                console.error('Erro ao carregar configuração do Agente Silva:', error);
            }
        };

        carregarConfig();
    }, [modulo, entidadeId]);

    /**
     * Rótulos das ferramentas, para o utilizador perceber o que o agente está a
     * fazer durante os segundos em que consulta a base de dados.
     */
    const rotuloDaFerramenta = (nome: string): string => {
        const rotulos: Record<string, string> = {
            listarFacturas: 'a consultar facturas',
            estatisticasFacturas: 'a somar a facturação',
            listarClientes: 'a consultar clientes',
            listarFuncionarios: 'a consultar funcionários',
            estatisticasFuncionarios: 'a contar funcionários',
            resumoAssiduidade: 'a ver a assiduidade',
            stockArtigos: 'a consultar o stock',
            consultarFolhaSalarial: 'a consultar a folha salarial',
            pesquisarAlunos: 'a procurar alunos',
            estatisticasAlunos: 'a contar alunos',
            propinasEmAtraso: 'a ver propinas em atraso',
            totalRecebidoPropinas: 'a somar as propinas recebidas',
            notasAluno: 'a consultar notas',
            resumoFaltas: 'a ver as presenças',
            pdfDaFactura: 'a procurar a factura',
            lembreteDePagamento: 'a preparar o lembrete',
            prepararProforma: 'a preparar a proforma',
            lembreteDePropinas: 'a preparar o lembrete das propinas',
        };

        return rotulos[nome] ?? 'a consultar dados';
    };

    /**
     * Envia a mensagem e mostra a resposta à medida que chega.
     *
     * O caminho normal (uma única resposta JSON) fica como alternativa: se o
     * servidor não devolver um fluxo — proxy sem suporte, erro antes de começar
     * — cai-se nele sem o utilizador dar por nada.
     */
    const enviarMensagem = useCallback(async (texto: string) => {
        // Uma pergunta de cada vez: o Enter e o botão chegavam a mandar a mesma duas vezes.
        if (!texto.trim() || aEnviar.current) return;

        aEnviar.current = true;
        setIsLoading(true);

        const idUtilizador = Date.now();
        const idAgente = idUtilizador + 1;

        // Uma mensagem nova responde à acção à espera: «sim» executa-a e «não» cancela-a (o
        // servidor diz qual, no `accao_decidida`); outra coisa qualquer deita-a fora. Os
        // botões dela deixam de valer.
        setMensagens(prev => [
            ...prev.map(m => (m.accao?.estado === 'pendente' ? { ...m, accao: { ...m.accao, estado: 'descartada' as const } } : m)),
            {
                id: idUtilizador,
                tipo: 'utilizador',
                texto,
                created_at: new Date().toISOString(),
            },
        ]);

        const corpo = {
            texto,
            conversa_id: conversaId,
            tenant_type: tenantType,
        };

        const actualizarAgente = (mudanca: Partial<Mensagem>) => {
            setMensagens(prev => prev.map(m => (m.id === idAgente ? { ...m, ...mudanca } : m)));
        };

        const limparOptimista = () => {
            setMensagens(prev => prev.filter(m => m.id !== idUtilizador && m.id !== idAgente));
        };

        /** O servidor decidiu a acção à espera (o utilizador escreveu «sim» ou «não»). */
        const marcarAccao = (decidida: Partial<AccaoSilva> & { id?: string } | null | undefined) => {
            if (!decidida?.id || !decidida.estado) return;
            setMensagens(prev => prev.map(m => (m.accao?.id === decidida.id ? { ...m, accao: { ...m.accao, estado: decidida.estado! } } : m)));
        };

        let fluxoComecou = false;
        let conversaDoFluxo: number | null = null;
        let acumulado = '';

        try {
            const resposta = await fetch(`${apiBase}/mensagem-stream`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify(corpo),
            });

            const tipo = resposta.headers.get('content-type') || '';

            if (!resposta.ok || !resposta.body || !tipo.includes('text/event-stream')) {
                const erro = await resposta.json().catch(() => null);

                if (erro?.creditos_ia) {
                    setCreditos(erro.creditos_ia);
                }

                if (erro?.texto) {
                    showPop('', erro.texto, 'error');
                    limparOptimista();
                    return;
                }

                throw new Error('sem fluxo');
            }

            // A partir daqui o servidor já gravou a pergunta: se o fluxo cair, não se volta a
            // enviar (ficava duas vezes, com duas respostas e dois pagamentos).
            fluxoComecou = true;

            const conversaDoCabecalho = resposta.headers.get('X-Agente-Silva-Conversa');
            conversaDoFluxo = conversaDoCabecalho ? Number(conversaDoCabecalho) : conversaId;
            const saldoDoCabecalho = resposta.headers.get('X-Creditos-Ia-Saldo');

            if (saldoDoCabecalho !== null) {
                setCreditos(c => (c ? { ...c, saldo: Number(saldoDoCabecalho) } : c));
            }

            if (!conversaId && conversaDoCabecalho) {
                setConversaId(Number(conversaDoCabecalho));
            }

            setMensagens(prev => [...prev, {
                id: idAgente,
                tipo: 'agente',
                texto: '',
                created_at: new Date().toISOString(),
                aEscrever: true,
            }]);

            const leitor = resposta.body.getReader();
            const descodificador = new TextDecoder();
            let porProcessar = '';

            while (true) {
                const { done, value } = await leitor.read();

                if (done) break;

                porProcessar += descodificador.decode(value, { stream: true });

                // Os eventos vêm separados por linha em branco (formato SSE).
                const blocos = porProcessar.split('\n\n');
                porProcessar = blocos.pop() ?? '';

                for (const bloco of blocos) {
                    const linha = bloco.split('\n').find(l => l.startsWith('data:'));

                    if (!linha) continue;

                    const carga = linha.slice(5).trim();

                    if (carga === '[DONE]') continue;

                    let evento: any;

                    try {
                        evento = JSON.parse(carga);
                    } catch {
                        continue;
                    }

                    if (evento.type === 'text_delta') {
                        acumulado += evento.delta ?? '';
                        actualizarAgente({ texto: acumulado, aConsultar: null });
                    } else if (evento.type === 'tool_call') {
                        actualizarAgente({ aConsultar: rotuloDaFerramenta(evento.tool_name) });
                    } else if (evento.type === 'tool_result') {
                        actualizarAgente({ aConsultar: null });
                    } else if (evento.type === 'accao_pendente' && evento.accao?.id) {
                        // O Silva preparou uma acção: os botões aparecem por baixo da resposta.
                        actualizarAgente({ accao: { ...evento.accao, estado: 'pendente' } });
                    } else if (evento.type === 'accao_decidida') {
                        marcarAccao(evento.accao);
                    } else if (evento.type === 'videos' && Array.isArray(evento.videos)) {
                        // Os vídeos de ajuda dos artigos que a resposta usou: aparecem por baixo dela.
                        actualizarAgente({ videos: evento.videos });
                    } else if (evento.type === 'artigos' && Array.isArray(evento.artigos)) {
                        // Os artigos da central de ajuda de onde vêm os passos.
                        actualizarAgente({ artigos: evento.artigos });
                    } else if (evento.type === 'error') {
                        throw new Error(evento.message || 'erro no fluxo');
                    }
                }
            }

            if (!acumulado.trim()) {
                throw new Error('fluxo vazio');
            }

            actualizarAgente({ aEscrever: false, aConsultar: null });
        } catch (error: any) {
            if (fluxoComecou) {
                // O fluxo caiu a meio. O servidor grava a resposta quando ela acaba (ou tira a
                // pergunta, se falhou): mostra-se o que lá está, em vez de perguntar outra vez.
                console.warn('Agente Silva: o fluxo foi interrompido; a conversa vem do servidor.', error);
                if (conversaDoFluxo) {
                    try {
                        const historico = await req().get(`${apiBase}/historico`, { params: { conversa_id: conversaDoFluxo } });
                        if (historico.data.estado === 'ok') {
                            setMensagens(comAccoesDoHistorico(historico.data.mensagens || []));
                        }
                    } catch {
                        // Fica o que já se via.
                    }
                }
                actualizarAgente({ aEscrever: false, aConsultar: null });
                if (!acumulado.trim()) {
                    showPop('', 'A resposta não chegou. Tenta de novo.', 'error');
                }
                return;
            }

            // Alternativa: pedir a resposta de uma vez.
            //
            // Fica registado na consola de propósito: quando o fluxo falha, o
            // chat continua a funcionar e o problema desaparece de vista. Já
            // aconteceu uma vez, e ninguém deu por isso.
            console.warn('Agente Silva: fluxo indisponível, a usar a resposta única.', error);

            setMensagens(prev => prev.filter(m => m.id !== idAgente));

            try {
                const response = await req().post(`${apiBase}/mensagem`, corpo);

                if (response.data.creditos_ia) {
                    setCreditos(response.data.creditos_ia);
                }

                if (response.data.estado === 'ok') {
                    if (!conversaId && response.data.conversa_id) {
                        setConversaId(response.data.conversa_id);
                    }

                    setMensagens(prev => [...prev, {
                        id: response.data.mensagem_id,
                        tipo: 'agente',
                        texto: response.data.resposta,
                        created_at: new Date().toISOString(),
                        accao: response.data.accao_pendente ? { ...response.data.accao_pendente, estado: 'pendente' } : null,
                        videos: Array.isArray(response.data.videos) ? response.data.videos : undefined,
                        artigos: Array.isArray(response.data.artigos) ? response.data.artigos : undefined,
                    }]);
                    marcarAccao(response.data.accao);
                } else {
                    showPop('', response.data.texto || 'Não foi possível enviar a mensagem. Tenta de novo.', 'error');
                    limparOptimista();
                }
            } catch (falha: any) {
                showPop('', falha.response?.data?.texto || 'Não foi possível comunicar com o Agente Silva. Tenta de novo.', 'error');
                limparOptimista();
            }
        } finally {
            aEnviar.current = false;
            setIsLoading(false);
        }
    }, [conversaId, tenantType, apiBase]);

    /**
     * «Confirmar» ou «Cancelar» por baixo de uma resposta do Silva. Quem executa é o servidor,
     * que volta a verificar tudo (dono, conversa, empresa, validade); aqui só se pede e se
     * mostra o resultado na conversa, como se o utilizador tivesse escrito «sim» ou «não».
     */
    const decidirAccao = useCallback(async (mensagemId: number, accao: AccaoSilva, confirmar: boolean) => {
        if (aDecidir || !conversaId) return;

        setADecidir({ accaoId: accao.id, confirmar });
        setIsLoading(true);

        const response = await req().post(`${apiBase}/accao/${accao.id}/${confirmar ? 'confirmar' : 'cancelar'}`, {
            conversa_id: conversaId,
            tenant_type: tenantType,
        });

        const dados = response.data as any;
        const estado: AccaoSilva['estado'] = dados?.accao?.estado
            ?? (dados?.estado === 'ok' ? (confirmar ? 'confirmada' : 'cancelada') : 'expirada');

        setMensagens(prev => {
            const actualizadas = prev.map(m => (m.id === mensagemId && m.accao ? { ...m, accao: { ...m.accao, estado } } : m));

            if (!dados?.resposta) {
                return actualizadas;
            }

            const agora = new Date().toISOString();

            return [
                ...actualizadas,
                { id: -Date.now(), tipo: 'utilizador' as const, texto: confirmar ? 'Confirmar' : 'Cancelar', created_at: agora },
                { id: dados.mensagem_id ?? Date.now(), tipo: 'agente' as const, texto: dados.resposta, created_at: agora },
            ];
        });

        // Sem resposta na conversa (expirou, outra conta): o motivo aparece num aviso.
        if (dados?.estado !== 'ok' && !dados?.resposta) {
            showPop('', dados?.texto || 'Não foi possível concluir o pedido. Tenta de novo.', 'error');
        }

        setADecidir(null);
        setIsLoading(false);
    }, [aDecidir, conversaId, apiBase, tenantType]);

    const carregarHistorico = useCallback(async () => {
        if (!conversaId) return;

        setIsLoading(true);

        try {
            const response = await req().get(`${apiBase}/historico`, {
                params: { conversa_id: conversaId }
            });

            if (response.data.estado === 'ok') {
                setMensagens(comAccoesDoHistorico(response.data.mensagens || []));
            }
        } catch (error) {
            console.error('Erro ao carregar histórico:', error);
        } finally {
            setIsLoading(false);
        }
    }, [conversaId]);

    const carregarConversas = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await req().get(`${apiBase}/conversas`, {
                params: {
                    tenant_id: entidadeId,
                    tenant_type: modulo === 'logigate' ? 'empresa' : 'escola',
                }
            });

            if (response.data.estado === 'ok') {
                setConversas(response.data.conversas || []);
            }
        } catch (error) {
            console.error('Erro ao carregar conversas:', error);
        } finally {
            setIsLoading(false);
        }
    }, [entidadeId, modulo]);

    const selecionarConversa = useCallback(async (conversa: Conversa) => {
        setIsLoading(true);
        setConversaId(conversa.id);

        try {
            const response = await req().get(`${apiBase}/historico`, {
                params: { conversa_id: conversa.id }
            });

            if (response.data.estado === 'ok') {
                setMensagens(comAccoesDoHistorico(response.data.mensagens || []));
                setIsHistoryView(false);
            }
        } catch (error) {
            console.error('Erro ao carregar conversa:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const limparConversa = useCallback(async () => {
        // Show confirmation dialog
        const confirmed = await showPopConfirm(
            'Limpar conversa?',
            'Tens a certeza de que queres limpar esta conversa? As mensagens serão arquivadas.',
            'question'
        );

        if (!confirmed) return;

        setIsLoading(true);

        // If we have an active conversation, archive it on the backend
        if (conversaId) {
            try {
                const response = await req().delete(`${apiBase}/conversa/${conversaId}`);
                if (response.data.estado !== 'ok') {
                    showPop("", response.data.texto || 'Não foi possível arquivar a conversa. Tenta de novo.', 'error');
                    setIsLoading(false);
                    return;
                }
            } catch (error: any) {
                console.error('Erro ao arquivar conversa:', error);
                showPop("", error.response?.data?.texto || 'Não foi possível arquivar a conversa. Tenta de novo.', 'error');
                setIsLoading(false);
                return;
            }
        }

        // Clear local state
        setMensagens([]);
        setConversaId(null);

        // Add welcome message back
        if (config?.welcome_message) {
            const welcomeMensagem: Mensagem = {
                id: 0,
                tipo: 'agente',
                texto: config.welcome_message,
                created_at: new Date().toISOString(),
            };
            setMensagens([welcomeMensagem]);
        }

        setIsLoading(false);
    }, [conversaId, config?.welcome_message, apiBase]);

    const iniciarNovaConversa = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await req().post(`${apiBase}/nova-conversa`, {
                tenant_id: entidadeId,
                tenant_type: modulo === 'logigate' ? 'empresa' : 'escola',
            });

            if (response.data.estado === 'ok') {
                setConversaId(response.data.conversa.id);
                setIsHistoryView(false); // Close history view

                // Add welcome message
                if (config?.welcome_message) {
                    const welcomeMensagem: Mensagem = {
                        id: 0,
                        tipo: 'agente',
                        texto: config.welcome_message,
                        created_at: new Date().toISOString(),
                    };
                    setMensagens([welcomeMensagem]);
                } else {
                    setMensagens([]);
                }
            }
        } catch (error) {
            console.error('Erro ao iniciar nova conversa:', error);
        } finally {
            setIsLoading(false);
        }
    }, [entidadeId, modulo, apiBase, config?.welcome_message]);

    const abrirChat = useCallback(() => setIsOpen(true), []);
    const fecharChat = useCallback(() => setIsOpen(false), []);
    const toggleChat = useCallback(() => setIsOpen(prev => !prev), []);
    const toggleHistoryView = useCallback(() => {
        setIsHistoryView(prev => {
            if (!prev) {
                carregarConversas();
            }
            return !prev;
        });
    }, [carregarConversas]);

    // Effect to add welcome message when chat opens for first time
    useEffect(() => {
        if (isOpen && mensagens.length === 0 && config?.welcome_message) {
            const welcomeMensagem: Mensagem = {
                id: 0,
                tipo: 'agente',
                texto: config.welcome_message,
                created_at: new Date().toISOString(),
            };
            setMensagens([welcomeMensagem]);
        }
    }, [isOpen, config?.welcome_message]);

    const value: AgenteSilvaContextValue = {
        modulo,
        mensagens,
        conversaId,
        conversas,
        isLoading,
        isOpen,
        isHistoryView,
        config,
        creditos,
        paginaCreditos: `${basePath}/creditos_ia`,
        enviarMensagem,
        decidirAccao,
        aDecidir,
        carregarHistorico,
        carregarConversas,
        selecionarConversa,
        limparConversa,
        iniciarNovaConversa,
        abrirChat,
        fecharChat,
        toggleChat,
        toggleHistoryView,
    };

    return (
        <AgenteSilvaContext.Provider value={value}>
            {children}
        </AgenteSilvaContext.Provider>
    );
}

export function useAgenteSilva() {
    const context = useContext(AgenteSilvaContext);
    if (!context) {
        throw new Error('useAgenteSilva must be used within AgenteSilvaProvider');
    }
    return context;
}

export default AgenteSilvaContext;
